// ============================================================================
// MONITOR EVENT STORE — In-memory ring buffer + KV polling (v2.0)
// ============================================================================
// Workers are stateless: SSE can't bridge cron ↔ HTTP invocations.
// Instead, cron persists events to KV and the frontend polls periodically.
// ============================================================================

const MAX_EVENTS = 5000;
const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds

/** @type {import('./MonitorEventStore').MonitorEvent[]} */
let events = [];
let listeners = new Set();
let nextId = 1;
let pollTimer = null;
let lastSeenTs = 0;
let connectionStatus = 'disconnected'; // 'connected' | 'connecting' | 'disconnected'
let statusListeners = new Set();
let currentStrategyFilter = null;
let consecutiveErrors = 0;

// ── Fingerprinting ──────────────────────────────────────────────────────
function computeFingerprint(event) {
  const key = `${event.source}:${event.category}:${event.action}:${event.message?.substring(0, 80)}`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
  }
  return 'fp_' + Math.abs(hash).toString(36);
}

// ── Core API ────────────────────────────────────────────────────────────

export function addEvent(partial) {
  const event = {
    id: `evt_${nextId++}_${Date.now()}`,
    ts: Date.now(),
    level: 'info',
    source: 'frontend',
    category: 'console',
    action: 'LOG',
    message: '',
    ...partial,
  };
  if (!event.fingerprint) {
    event.fingerprint = computeFingerprint(event);
  }

  events.push(event);
  // Ring buffer — drop oldest
  if (events.length > MAX_EVENTS) {
    events = events.slice(events.length - MAX_EVENTS);
  }
  // Notify subscribers
  listeners.forEach(fn => {
    try { fn(event); } catch (_) {}
  });
  return event;
}

export function getEvents() {
  return events;
}

export function clearEvents() {
  events = [];
  listeners.forEach(fn => {
    try { fn(null); } catch (_) {}
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getConnectionStatus() {
  return connectionStatus;
}

export function subscribeStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

function setConnectionStatus(status) {
  connectionStatus = status;
  statusListeners.forEach(fn => {
    try { fn(status); } catch (_) {}
  });
}

// ── Polling Connection to Backend ────────────────────────────────────────

const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';

async function pollEvents() {
  try {
    const params = new URLSearchParams({ limit: '500' });
    if (lastSeenTs > 0) params.set('since', String(lastSeenTs));
    if (currentStrategyFilter) params.set('strategyId', currentStrategyFilter);

    const token = localStorage.getItem('auth_token');
    const resp = await fetch(`${STRATEGY_ENGINE_URL}/api/monitor/events?${params}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'X-User-Id': localStorage.getItem('user_id') || 'default-user',
      },
    });
    if (!resp.ok) {
      consecutiveErrors++;
      if (consecutiveErrors >= 3) setConnectionStatus('disconnected');
      return;
    }

    const data = await resp.json();
    consecutiveErrors = 0;
    setConnectionStatus('connected');

    const backendEvents = data.events || [];
    if (backendEvents.length === 0) return;

    // Merge into local store (avoid duplicates)
    const existingIds = new Set(events.map(e => e.id));
    let added = 0;
    for (const ev of backendEvents) {
      if (!existingIds.has(ev.id)) {
        events.push({ ...ev, source: ev.source || 'backend' });
        added++;
        if (ev.ts > lastSeenTs) lastSeenTs = ev.ts;
      }
    }

    if (added > 0) {
      events.sort((a, b) => a.ts - b.ts);
      if (events.length > MAX_EVENTS) {
        events = events.slice(events.length - MAX_EVENTS);
      }
      // Notify subscribers
      listeners.forEach(fn => { try { fn(null); } catch (_) {} });
    }
  } catch (err) {
    consecutiveErrors++;
    if (consecutiveErrors >= 3) setConnectionStatus('disconnected');
  }
}

export function connectSSE(strategyId = null) {
  disconnectSSE();
  currentStrategyFilter = strategyId;
  lastSeenTs = 0;
  setConnectionStatus('connecting');

  // Initial full fetch
  pollEvents().then(() => {
    // Start periodic polling
    pollTimer = setInterval(pollEvents, POLL_INTERVAL_MS);
  });
}

export function disconnectSSE() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  setConnectionStatus('disconnected');
}

export async function fetchRecentEvents(strategyId = null, limit = 500) {
  // Just trigger a poll — the polling loop handles everything
  currentStrategyFilter = strategyId;
  await pollEvents();
}

// ── Export / Copy ───────────────────────────────────────────────────────

export function exportIssues(eventsToExport, options = {}) {
  const { strategyId, runId, timeWindow } = options;
  const issues = eventsToExport.filter(e => e.level === 'error' || e.level === 'warn');

  const lines = [
    '# Strategy Monitor — Issue Report',
    `**Generated**: ${new Date().toISOString()}`,
    `**Browser**: ${navigator.userAgent}`,
    strategyId ? `**Strategy**: ${strategyId}` : '',
    runId ? `**Run**: ${runId}` : '',
    timeWindow ? `**Time Window**: ${timeWindow}` : '',
    `**Total Issues**: ${issues.length}`,
    '',
    '---',
    '',
  ];

  for (const ev of issues) {
    const time = new Date(ev.ts).toISOString();
    lines.push(`### [${ev.level.toUpperCase()}] ${ev.action}`);
    lines.push(`- **Time**: ${time}`);
    lines.push(`- **Source**: ${ev.source} / ${ev.category}`);
    if (ev.symbol) lines.push(`- **Symbol**: ${ev.symbol}`);
    if (ev.strategyId) lines.push(`- **Strategy**: ${ev.strategyId}`);
    lines.push(`- **Message**: ${ev.message}`);
    if (ev.data?.stack) lines.push(`\`\`\`\n${ev.data.stack}\n\`\`\``);
    if (ev.data && !ev.data.stack) {
      const safeData = { ...ev.data };
      delete safeData.apiKey;
      delete safeData.secret;
      delete safeData.token;
      lines.push(`\`\`\`json\n${JSON.stringify(safeData, null, 2)}\n\`\`\``);
    }
    lines.push('');
  }

  return lines.filter(Boolean).join('\n');
}
